import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Eye, Globe, KeyRound, LogOut, MonitorCheck, MonitorOff, MonitorPlay, Radio, ShieldOff } from 'lucide-react';
import type {
  ClinicalPurgePreview,
  OwnerTenantDeploymentDto,
  OwnerTenantUser,
  OwnerWatchSessionDto,
  PurgePreview,
  PurgeResult,
  TempPasswordResult,
} from '@his/shared';
import { del, get, post, put, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { DataTable, FilterField, SearchFilterBar } from '@/components/ui/layout';
import {
  Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Field, Input,
} from '@/components/ui/primitives';
import { OwnerConfirmDialog, TempPasswordDialog } from './OwnerConfirm';
import { capabilityAllows } from '@his/shared';
import { ConsequencePreview } from './ConsequencePreview';
import { SignInAsDialog, TenantSupportAccessCard } from './SignInAsDialog';
import { LiveSessionDialog } from './LiveSessionDialog';

/**
 * The Owner's write access to a hospital (SRS 5.1, 5.3): edit its commercial
 * record, work the support desk on its users, and — last and hardest — take it
 * off the platform.
 *
 * Removal is three separate things, because "remove this" means three different
 * things depending on who is asking. A lapsed subscription stops writes. An
 * archive stops everything but keeps every row. A purge destroys the records,
 * and only ever after the platform has sealed a copy it cannot itself open.
 */

// ── Users ───────────────────────────────────────────────────────────────────

type UserAction = 'deactivate' | 'activate' | 'reset-password' | 'reset-2fa' | 'force-logout' | 'cloud-login-allow' | 'cloud-login-deny' | 'watch-allow' | 'watch-deny';

function isQueuedForLocalServer(result: unknown): result is { queued: true; message: string } {
  return typeof result === 'object' && result !== null && (result as { queued?: unknown }).queued === true;
}

const ACTION_COPY: Record<
  UserAction,
  { title: string; warning: string; verb: string; destructive?: boolean }
> = {
  deactivate: {
    title: 'Deactivate this account',
    warning: 'The user is signed out everywhere and cannot sign back in.',
    verb: 'Deactivate',
    destructive: true,
  },
  activate: {
    title: 'Reactivate this account',
    warning: 'The user can sign in again with their existing password.',
    verb: 'Reactivate',
  },
  'reset-password': {
    title: 'Reset this password',
    warning:
      'A one-time password is generated and shown once. The user must change it at their next sign-in, and every current session ends.',
    verb: 'Reset password',
    destructive: true,
  },
  'reset-2fa': {
    title: 'Reset two-factor authentication',
    warning:
      'Clears the lost authenticator. The user enrols a new device at their next sign-in — verify who you are speaking to first.',
    verb: 'Reset 2FA',
    destructive: true,
  },
  'force-logout': {
    title: 'Sign this user out everywhere',
    warning: 'Ends every live session. The password is unchanged.',
    verb: 'Sign out',
  },
  'cloud-login-allow': {
    title: 'Allow sign-in on the online copy',
    warning:
      'This user can sign in here, on the web, with the same username, password and authenticator they use on the facility\'s own server. The online copy is read-only.',
    verb: 'Allow web sign-in',
  },
  'cloud-login-deny': {
    title: 'Refuse sign-in on the online copy',
    warning: 'Ends this user\'s sessions here and keeps them out of the web copy. Their work on the facility\'s own server is unaffected.',
    verb: 'Refuse web sign-in',
    destructive: true,
  },
  'watch-allow': {
    title: 'Allow screen watching',
    warning:
      'The Owner may then open a thirty-minute watch session on this user\'s screen. The user always sees a red banner while it runs, typed values and passwords are hidden, and control is only ever given when the user accepts.',
    verb: 'Allow watching',
  },
  'watch-deny': {
    title: 'Refuse screen watching',
    warning: 'Any open watch session on this user ends now, and no new one can start.',
    verb: 'Refuse watching',
    destructive: true,
  },
};

export function TenantUsersPanel({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const { user: viewer } = useAuth();
  const navigate = useNavigate();
  const canSignInAs = capabilityAllows('tenant.sign_in_as', viewer?.platformRole);
  const canSeeSupportAccess = capabilityAllows('tenant.support_session.read', viewer?.platformRole);
  const canLiveSession = capabilityAllows('owner.live_session', viewer?.platformRole);
  const canWatch = capabilityAllows('owner.watch_user', viewer?.platformRole);
  const [signingInAs, setSigningInAs] = useState<OwnerTenantUser | null>(null);
  const [liveFor, setLiveFor] = useState<OwnerTenantUser | null>(null);
  const [watching, setWatching] = useState<OwnerTenantUser | null>(null);
  const [q, setQ] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [pending, setPending] = useState<{ user: OwnerTenantUser; action: UserAction } | null>(null);
  const [issued, setIssued] = useState<{ who: string; username: string; password: string } | null>(null);

  const users = useQuery({
    queryKey: ['tenant-users', tenantId, q, includeInactive],
    queryFn: () =>
      get<OwnerTenantUser[]>(
        `/platform/tenants/${tenantId}/users${qs({ q: q || undefined, includeInactive })}`,
      ),
  });

  const deployment = useQuery({
    queryKey: ['tenant-deployment', tenantId],
    queryFn: () => get<OwnerTenantDeploymentDto>(`/owner/tenants/${tenantId}/deployment`),
  });
  const offline = deployment.data?.deploymentMode === 'LOCAL';
  const linked = deployment.data?.linkConnected === true;

  const copy = pending ? ACTION_COPY[pending.action] : null;

  return (
    <>
      <Alert className="mb-4">
        Support actions only. Nothing here opens a patient record — the Owner console cannot read
        clinical data — and each one asks for your 2FA code and lands in the audit trail with the
        reason you give.
      </Alert>

      <SearchFilterBar
        search={{ value: q, onChange: setQ, placeholder: 'Search by name or email' }}
        filters={
          <FilterField className="justify-end pb-2">
            <Checkbox checked={includeInactive} onCheckedChange={setIncludeInactive} label="Show deactivated accounts" />
          </FilterField>
        }
        onReset={
          q || includeInactive
            ? () => {
                setQ('');
                setIncludeInactive(false);
              }
            : undefined
        }
      />

      <DataTable
        rows={users.data}
        loading={users.isLoading} refreshing={users.isFetching}
        error={users.error}
        rowKey={(row) => row.id}
        empty="No users match that search."
        columns={[
          {
            header: 'User',
            sort: (row) => row.name,
            cell: (row) => (
              <div>
                <div className="flex items-center gap-1.5 font-medium">
                  {row.name}
                  {row.isSuperAdmin && <Badge variant="secondary">Super Admin</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">
                  <span className="font-mono">{row.username}</span> · {row.email ?? row.phone ?? '—'}
                </div>
              </div>
            ),
          },
          { header: 'Roles', sort: (row) => row.roles.join(', '), cell: (row) => (row.roles.length ? row.roles.join(', ') : '—') },
          {
            header: 'State',
            sort: (row) => row.isActive,
            cell: (row) => (
              <div className="flex flex-wrap gap-1">
                <Badge variant={row.isActive ? 'default' : 'muted'}>
                  {row.isActive ? 'Active' : 'Deactivated'}
                </Badge>
                {row.has2fa && <Badge variant="muted">2FA</Badge>}
                {offline && (row.cloudLoginAllowed ? <Badge variant="success">Web sign-in</Badge> : <Badge variant="muted">No web sign-in</Badge>)}
                {row.watchAllowed && <Badge variant="muted">Watchable</Badge>}
                {row.mustChangePassword && <Badge variant="muted">Password reset pending</Badge>}
                {row.lockedUntil && new Date(row.lockedUntil) > new Date() && (
                  <Badge variant="destructive">Locked out</Badge>
                )}
              </div>
            ),
          },
          {
            header: 'Sessions',
            align: 'right',
            sort: (row) => row.activeSessions,
            cell: (row) => <span className="tabular">{row.activeSessions}</span>,
          },
          { header: 'Last sign-in', sort: (row) => row.lastLoginAt, cell: (row) => (row.lastLoginAt ? dateTime(row.lastLoginAt) : 'Never') },
          {
            header: 'Support',
            align: 'right',
            cell: (row) => (
              <div className="flex flex-wrap justify-end gap-1">
                {canSignInAs && (
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Sign in as this user (read-only)"
                    disabled={!row.isActive}
                    onClick={() => setSigningInAs(row)}
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                )}
                {offline && canLiveSession && (
                  <Button size="sm" variant="ghost" title="Live session on the facility's own server (read-only)" disabled={!row.isActive} onClick={() => setLiveFor(row)}>
                    <Radio className="h-3.5 w-3.5" />
                  </Button>
                )}
                {canWatch && row.watchAllowed && (
                  <Button
                    size="sm"
                    variant="ghost"
                    title={offline && !linked ? "This facility's server is not connected to the cloud right now" : "Watch this user's screen"}
                    disabled={!row.isActive || (offline && !linked)}
                    onClick={() => setWatching(row)}
                  >
                    <MonitorPlay className="h-3.5 w-3.5" />
                  </Button>
                )}
                {canWatch && (
                  <Button
                    size="sm"
                    variant="ghost"
                    title={row.watchAllowed ? 'Refuse screen watching' : 'Allow screen watching'}
                    onClick={() => setPending({ user: row, action: row.watchAllowed ? 'watch-deny' : 'watch-allow' })}
                  >
                    {row.watchAllowed ? <MonitorOff className="h-3.5 w-3.5" /> : <MonitorCheck className="h-3.5 w-3.5" />}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  title="Issue a one-time password"
                  onClick={() => setPending({ user: row, action: 'reset-password' })}
                >
                  <KeyRound className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  title="Reset two-factor authentication"
                  disabled={!row.has2fa}
                  onClick={() => setPending({ user: row, action: 'reset-2fa' })}
                >
                  <ShieldOff className="h-3.5 w-3.5" />
                </Button>
                {offline && (
                  <Button
                    size="sm"
                    variant="ghost"
                    title={row.cloudLoginAllowed ? 'Refuse sign-in on the online copy' : 'Allow sign-in on the online copy'}
                    onClick={() => setPending({ user: row, action: row.cloudLoginAllowed ? 'cloud-login-deny' : 'cloud-login-allow' })}
                  >
                    <Globe className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  title="Sign out everywhere"
                  disabled={row.activeSessions === 0}
                  onClick={() => setPending({ user: row, action: 'force-logout' })}
                >
                  <LogOut className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant={row.isActive ? 'destructive' : 'secondary'}
                  onClick={() =>
                    setPending({ user: row, action: row.isActive ? 'deactivate' : 'activate' })
                  }
                >
                  {row.isActive ? 'Deactivate' : 'Reactivate'}
                </Button>
              </div>
            ),
          },
        ]}
      />

      {pending && copy && (
        <OwnerConfirmDialog
          open
          onClose={() => setPending(null)}
          title={copy.title}
          description={pending.user.name}
          warning={copy.warning}
          confirmLabel={copy.verb}
          destructive={copy.destructive}
          successMessage={(result: TempPasswordResult) =>
            isQueuedForLocalServer(result)
              ? `${copy.verb} queued — ${pending.user.name}. ${result.message}`
              : pending.action === 'reset-password'
                ? undefined
                : `${copy.verb} — ${pending.user.name}`
          }
          action={(payload) => {
            const changesWebSignIn = pending.action === 'cloud-login-allow' || pending.action === 'cloud-login-deny';
            const changesWatching = pending.action === 'watch-allow' || pending.action === 'watch-deny';
            if (changesWatching) {
              return put<TempPasswordResult>(`/platform/tenants/${tenantId}/users/${pending.user.id}/watch-allowed`, { ...payload, allowed: pending.action === 'watch-allow' });
            }
            return post<TempPasswordResult>(
              `/platform/tenants/${tenantId}/users/${pending.user.id}/${changesWebSignIn ? 'cloud-login' : pending.action}`,
              changesWebSignIn ? { ...payload, allowed: pending.action === 'cloud-login-allow' } : payload,
            );
          }}
          onDone={(result) => {
            void queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] });
            if (pending.action === 'reset-password') {
              // Shown once and never again, so it gets its own dialog.
              setIssued({ who: pending.user.name, username: result.username, password: result.temporaryPassword });
            }
            setPending(null);
          }}
        />
      )}

      {issued && (
        <TempPasswordDialog
          open
          who={issued.who}
          username={issued.username}
          password={issued.password}
          onClose={() => setIssued(null)}
        />
      )}

      {signingInAs && (
        <SignInAsDialog tenantId={tenantId} user={{ id: signingInAs.id, name: signingInAs.name }} onClose={() => setSigningInAs(null)} />
      )}

      {liveFor && <LiveSessionDialog tenantId={tenantId} user={{ id: liveFor.id, name: liveFor.name }} offline={offline} linked={linked} onClose={() => setLiveFor(null)} />}

      {watching && (
        <OwnerConfirmDialog
          open
          onClose={() => setWatching(null)}
          title={`Watch ${watching.name}'s screen`}
          description="Thirty minutes at most. The user sees a red banner the whole time; passwords and typed values are hidden; control only if they accept."
          confirmLabel="Start watching"
          action={(payload) => post<OwnerWatchSessionDto>(`/platform/tenants/${tenantId}/users/${watching.id}/watch`, payload)}
          onDone={(started) => {
            setWatching(null);
            navigate(`/owner/tenants/${tenantId}/watch/${started.sessionId}`);
          }}
        />
      )}

      {canSeeSupportAccess && <TenantSupportAccessCard tenantId={tenantId} canEnd={canSignInAs} />}
    </>
  );
}

// ── Danger zone ─────────────────────────────────────────────────────────────

type DangerAction = 'archive' | 'restore' | 'expire' | 'renew' | 'clinical' | 'purge' | 'list' | 'unlist';

export function TenantDangerZone({
  tenantId,
  slug,
  status,
  directoryEnabled,
}: {
  tenantId: string;
  slug: string;
  status: string;
  directoryEnabled: boolean;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [action, setAction] = useState<DangerAction | null>(null);
  const [confirmSlug, setConfirmSlug] = useState('');

  const archived = status === 'archived';
  const lapsed = status === 'past_due';
  const isOwner = user?.platformRole === 'owner';
  const canDirectory = capabilityAllows('tenant.directory.update', user?.platformRole);
  const canSubscription = capabilityAllows('tenant.subscription.update', user?.platformRole);
  const canArchive = capabilityAllows('tenant.archive', user?.platformRole);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
    void queryClient.invalidateQueries({ queryKey: ['tenants'] });
    void queryClient.invalidateQueries({ queryKey: ['purge-preview', tenantId] });
    setAction(null);
    setConfirmSlug('');
  };

  const purgePreview = useQuery({
    queryKey: ['purge-preview', tenantId],
    queryFn: () => get<PurgePreview>(`/platform/tenants/${tenantId}/purge-preview`),
    enabled: isOwner && action === 'purge',
  });

  const clinicalPreview = useQuery({
    queryKey: ['clinical-purge-preview', tenantId],
    queryFn: () => get<ClinicalPurgePreview>(`/platform/tenants/${tenantId}/clinical-purge-preview`),
    enabled: isOwner && action === 'clinical',
  });

  return (
    <div className="space-y-4">
      {canDirectory && (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Public directory</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            {directoryEnabled
              ? 'This hospital may appear on the public directory. Which of its branches actually show is its own choice; this switch decides whether any of them can.'
              : 'Hidden from the public directory. Its branches keep everything they published — turning this back on restores the listing exactly as it was.'}
          </p>
          <Button
            variant={directoryEnabled ? 'secondary' : 'default'}
            onClick={() => setAction(directoryEnabled ? 'unlist' : 'list')}
          >
            {directoryEnabled ? 'Hide from the directory' : 'Show on the directory'}
          </Button>
        </CardContent>
      </Card>
      )}

      {canSubscription && (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Subscription</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            {lapsed
              ? 'Marked lapsed: the hospital can read everything and change nothing.'
              : 'Marking the subscription lapsed stops new records but leaves every existing one readable. An unpaid invoice must never stop a clinician reading a chart.'}
          </p>
          <Button variant={lapsed ? 'default' : 'secondary'} onClick={() => setAction(lapsed ? 'renew' : 'expire')}>
            {lapsed ? 'Renew subscription' : 'Mark subscription lapsed'}
          </Button>
        </CardContent>
      </Card>
      )}

      {canArchive && (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{archived ? 'Restore' : 'Archive'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            {archived
              ? 'Every record is intact. Restoring makes the hospital active again and its users can sign in immediately.'
              : 'Archiving ends every session and blocks sign-in, but keeps all data. This is what "remove this hospital" almost always means, and it can be undone.'}
          </p>
          <Button
            variant={archived ? 'default' : 'destructive'}
            onClick={() => setAction(archived ? 'restore' : 'archive')}
          >
            {archived ? 'Restore hospital' : 'Archive hospital'}
          </Button>
        </CardContent>
      </Card>
      )}

      {isOwner && (
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-sm">Clear clinical data</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Deletes patients, encounters, prescriptions, results and invoices. Staff accounts, roles,
            branches, the charge master and stock all survive — this is the pilot-to-live cleanup, not
            a deletion of the hospital.
          </p>
          {!isOwner && <Alert variant="warning">Only the platform Owner can clear clinical data.</Alert>}
          <Button variant="destructive" disabled={!isOwner} onClick={() => setAction('clinical')}>
            Review what would be cleared…
          </Button>
        </CardContent>
      </Card>
      )}

      {isOwner && (
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" /> Permanently delete
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Destroys every patient, prescription, result and invoice belonging to this hospital. Before
            anything is deleted the platform seals an encrypted copy of every row with the owner&apos;s
            escrow key — a key this server cannot open — and refuses the whole operation if it cannot.
            The audit trail also survives: it is append-only in the database.
          </p>
          {!isOwner && <Alert variant="warning">Only the platform Owner can run a purge.</Alert>}
          {!archived && (
            <Alert>
              Archive it first if you can. Nothing forces you to, but an archived hospital gives anyone
              still working in it a chance to notice before the records are gone.
            </Alert>
          )}
          <Button variant="destructive" disabled={!isOwner} onClick={() => setAction('purge')}>
            Review what would be deleted…
          </Button>
        </CardContent>
      </Card>
      )}

      {(action === 'list' || action === 'unlist') && (
        <OwnerConfirmDialog
          open
          onClose={() => setAction(null)}
          title={
            action === 'unlist'
              ? 'Hide this hospital from the directory'
              : 'Show this hospital on the directory'
          }
          description={slug}
          destructive={action === 'unlist'}
          confirmLabel={action === 'unlist' ? 'Hide' : 'Show'}
          successMessage={
            action === 'unlist'
              ? 'Hidden from the public directory'
              : 'Showing on the public directory'
          }
          warning={
            action === 'unlist'
              ? 'Patients can no longer find this hospital, and its branch pages stop answering. Nothing it published is deleted.'
              : 'Whatever this hospital has published becomes visible to the public again immediately.'
          }
          action={(payload) =>
            put(`/platform/tenants/${tenantId}/directory-visibility`, {
              ...payload,
              visible: action === 'list',
            })
          }
          onDone={refresh}
        />
      )}

      {(action === 'archive' || action === 'restore') && (
        <OwnerConfirmDialog
          open
          onClose={() => setAction(null)}
          title={action === 'archive' ? 'Archive this hospital' : 'Restore this hospital'}
          description={slug}
          destructive={action === 'archive'}
          confirmLabel={action === 'archive' ? 'Archive' : 'Restore'}
          successMessage={action === 'archive' ? 'Hospital archived' : 'Hospital restored'}
          warning={
            action === 'archive'
              ? 'Everyone is signed out immediately and nobody can sign back in. No data is deleted.'
              : 'The hospital becomes active again and its users can sign in at once.'
          }
          action={(payload) => post(`/platform/tenants/${tenantId}/${action}`, payload)}
          onDone={refresh}
        />
      )}

      {(action === 'expire' || action === 'renew') && (
        <OwnerConfirmDialog
          open
          onClose={() => setAction(null)}
          title={action === 'expire' ? 'Mark the subscription lapsed' : 'Renew the subscription'}
          description={slug}
          destructive={action === 'expire'}
          confirmLabel={action === 'expire' ? 'Mark lapsed' : 'Renew'}
          successMessage={action === 'expire' ? 'Subscription marked lapsed' : 'Subscription renewed'}
          warning={
            action === 'expire'
              ? 'Writes stop across the whole hospital. Reading carries on as normal.'
              : 'Normal access resumes immediately.'
          }
          action={(payload) => post(`/platform/tenants/${tenantId}/subscription/${action}`, payload)}
          onDone={refresh}
        >
          {action === 'expire' && <ConsequencePreview tenantId={tenantId} />}
        </OwnerConfirmDialog>
      )}

      {action === 'clinical' && (
        <OwnerConfirmDialog
          open
          wide
          onClose={() => {
            setAction(null);
            setConfirmSlug('');
          }}
          title="Clear clinical data"
          description={slug}
          destructive
          minReason={10}
          confirmLabel={`Clear ${clinicalPreview.data?.totalRows.toLocaleString() ?? ''} rows`}
          successMessage="Clinical data cleared"
          extraValid={confirmSlug.trim() === slug && (clinicalPreview.data?.totalRows ?? 0) > 0}
          warning="Patients and everything hanging off them go. Staff, roles, branches, the charge master and stock stay."
          action={(payload) =>
            del(`/platform/tenants/${tenantId}/clinical-data`, { body: { ...payload, confirmSlug } })
          }
          onDone={refresh}
        >
          <div className="space-y-3">
            <RowCounts rows={clinicalPreview.data?.rows} loading={clinicalPreview.isLoading} />
            {clinicalPreview.data && (
              <p className="text-xs text-muted-foreground">
                {clinicalPreview.data.preservedTables.length} other tables are left untouched.
              </p>
            )}
            <SlugConfirm slug={slug} value={confirmSlug} onChange={setConfirmSlug} />
          </div>
        </OwnerConfirmDialog>
      )}

      {action === 'purge' && (
        <OwnerConfirmDialog<PurgeResult>
          open
          wide
          onClose={() => {
            setAction(null);
            setConfirmSlug('');
          }}
          title="Permanently delete this facility"
          description={slug}
          destructive
          minReason={10}
          confirmLabel={`Delete ${purgePreview.data?.totalRows.toLocaleString() ?? ''} rows forever`}
          successMessage="Hospital purged — the escrow backup has been kept"
          extraValid={confirmSlug.trim() === slug && purgePreview.data?.purgeable === true}
          warning="There is no undo. The sealed escrow copy is the only way back, and only the holder of the private key can open it."
          action={(payload) =>
            del<PurgeResult>(`/platform/tenants/${tenantId}`, { body: { ...payload, confirmSlug } })
          }
          onDone={refresh}
        >
          <div className="space-y-3">
            {purgePreview.data && !purgePreview.data.purgeable && (
              <Alert variant="destructive">{purgePreview.data.blockedReason}</Alert>
            )}
            <RowCounts rows={purgePreview.data?.rows} loading={purgePreview.isLoading} />
            {purgePreview.data && (
              <p className="text-xs text-muted-foreground">
                {purgePreview.data.auditRowsRetained.toLocaleString()} audit rows are kept — the trail
                cannot be deleted by anyone, which is what makes it worth having. The escrow backup
                includes a copy of it.
              </p>
            )}
            <SlugConfirm slug={slug} value={confirmSlug} onChange={setConfirmSlug} />
          </div>
        </OwnerConfirmDialog>
      )}
    </div>
  );
}

function RowCounts({ rows, loading }: { rows?: { table: string; rows: number }[]; loading: boolean }) {
  if (loading) return <p className="text-sm text-muted-foreground">Counting…</p>;
  if (!rows || rows.length === 0)
    return <p className="text-sm text-muted-foreground">There is nothing to delete.</p>;

  return (
    <div className="max-h-56 overflow-y-auto rounded-md border">
      <table className="w-full text-sm">
        <tbody>
          {rows.map((row) => (
            <tr key={row.table} className="border-b last:border-0">
              <td className="px-3 py-1 text-muted-foreground">{row.table}</td>
              <td className="px-3 py-1 text-end tabular">{row.rows.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SlugConfirm({
  slug,
  value,
  onChange,
}: {
  slug: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={`Type ${slug} to confirm`} required>
      <Input value={value} onChange={(e) => onChange(e.target.value)} autoComplete="off" />
    </Field>
  );
}
